/**
 * Draft checkout validation aligned with ProductPage / MediaLinksSummary / MissedOpportunity flows.
 */

export function isValidURL(url) {
  const value = String(url || "").trim().toLowerCase();
  if (!value) return false;

  let isValid = "";
  const match = value.match(/(?:https:\/\/)?(?:www\.)?(.*?)\./);
  const extractedValue = match ? match[1] : "";
  if (/^w+$/.test(extractedValue) && extractedValue.length > 3) {
    isValid = false;
  } else if (/^w+$/.test(extractedValue) && extractedValue.length === 3) {
    const urlPattern =
      /^(?:(?:(?:https?|ftp):\/\/)?(?:www\.)?)?(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,}))(?:\/(?:[^\s/]*\/)?)?)?(?:\?(?:\S*))?(?:\.(?![^\s.]*$))?$/i;
    isValid = urlPattern.test(value);
  } else {
    const urlPattern =
      /^(?:(?:https?|ftp):\/\/)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:(?!www)[/?#]\S*)?$/;
    isValid = urlPattern.test(value);
  }
  return !!isValid;
}

function nestedLink(row) {
  return row?.link && typeof row.link === "object" ? row.link : row;
}

function filledValue(row, nested, keys) {
  for (const key of keys) {
    const value = row?.[key] ?? nested?.[key];
    if (value != null && String(value).trim() !== "") {
      return String(value).trim();
    }
  }
  return "";
}

function hasProduct(row, nested) {
  if (row?.product || nested?.product || row?.metricProduct || nested?.metricProduct) {
    return true;
  }
  return filledValue(row, nested, ["product_name"]) !== "";
}

function hasCategory(row, nested) {
  if (row?.primary_category?.id || nested?.primary_category?.id) {
    return true;
  }
  return filledValue(row, nested, ["category_name"]) !== "";
}

function flattenMissedOpportunityLinks(links) {
  const flat = [];

  (links || []).forEach((group) => {
    if (!group || typeof group !== "object") return;

    if (
      group.url ||
      group.destination_url ||
      group.text ||
      group.anchor_text ||
      group.opportunity_list_id
    ) {
      flat.push(group);
      return;
    }

    Object.values(group).forEach((items) => {
      if (!Array.isArray(items)) return;
      items.forEach((item) => {
        if (item && typeof item === "object") {
          flat.push(item);
        }
      });
    });
  });

  return flat;
}

function draftPrefix(draftOrder) {
  const id = Number(draftOrder?.id || 0);
  return id > 0 ? `Draft #${id}` : "Draft";
}

function draftError(draftOrder, message) {
  return `${draftPrefix(draftOrder)} - ${message}`;
}

function validateNormalLinks(links, draftOrder) {
  if (!Array.isArray(links) || !links.length) {
    return {
      valid: false,
      message: draftError(draftOrder, "Please review and fill all the required fields!"),
    };
  }

  let missingRequired = 0;
  let invalidUrl = 0;

  links.forEach((row) => {
    if (!row || typeof row !== "object") {
      missingRequired += 1;
      return;
    }

    const nested = nestedLink(row);
    if (!hasProduct(row, nested)) missingRequired += 1;
    if (!hasCategory(row, nested)) missingRequired += 1;
    if (!filledValue(row, nested, ["text", "anchor_text"])) missingRequired += 1;
    if (!filledValue(row, nested, ["secondry_category", "secondary_category"])) {
      missingRequired += 1;
    }

    const destination = filledValue(row, nested, [
      "url",
      "destination_url",
      "destinationUrl",
      "destination_link",
    ]);
    if (!destination) {
      missingRequired += 1;
      return;
    }
    if (!isValidURL(destination)) {
      invalidUrl += 1;
    }
  });

  if (missingRequired > 0) {
    return {
      valid: false,
      message: draftError(draftOrder, "Please review and fill all the required fields!"),
    };
  }
  if (invalidUrl > 0) {
    return {
      valid: false,
      message: draftError(draftOrder, "Please review and enter a valid destination URL!"),
    };
  }

  return { valid: true };
}

function validateMediaLinks(links, draftOrder) {
  if (!Array.isArray(links) || !links.length) {
    return {
      valid: false,
      message: draftError(draftOrder, "Please review and fill all the required fields!"),
    };
  }

  let missingRequired = 0;
  let invalidUrl = 0;

  links.forEach((row) => {
    if (!row || typeof row !== "object") {
      missingRequired += 1;
      return;
    }

    const nested = nestedLink(row);
    const destination = filledValue(row, nested, [
      "url",
      "destination_url",
      "destinationUrl",
    ]);
    const anchor = filledValue(row, nested, ["text", "anchor_text"]);

    if (!destination) missingRequired += 1;
    if (!anchor) missingRequired += 1;
    if (destination && !isValidURL(destination)) invalidUrl += 1;
  });

  if (missingRequired > 0) {
    return {
      valid: false,
      message: draftError(draftOrder, "Please review and fill all the required fields!"),
    };
  }
  if (invalidUrl > 0) {
    return {
      valid: false,
      message: draftError(draftOrder, "Please review and enter a valid destination URL!"),
    };
  }

  return { valid: true };
}

function validateMissedOpportunityLinks(links, draftOrder) {
  const flat = flattenMissedOpportunityLinks(links);
  if (!flat.length) {
    return {
      valid: false,
      message: draftError(draftOrder, "Please review and fill all the required fields!"),
    };
  }

  let missingRequired = 0;
  let invalidUrl = 0;

  flat.forEach((row) => {
    const nested = nestedLink(row);
    const destination = filledValue(row, nested, [
      "url",
      "destination_url",
      "destinationUrl",
      "destination_link",
    ]);
    const anchor = filledValue(row, nested, ["text", "anchor_text"]);

    if (!destination) missingRequired += 1;
    if (!anchor) missingRequired += 1;
    if (destination && !isValidURL(destination)) invalidUrl += 1;
  });

  if (missingRequired > 0) {
    return {
      valid: false,
      message: draftError(draftOrder, "Please review and fill all the required fields!"),
    };
  }
  if (invalidUrl > 0) {
    return {
      valid: false,
      message: draftError(draftOrder, "Please review and enter a valid destination URL!"),
    };
  }

  return { valid: true };
}

export function getDraftInvoiceReady({ order_title, order_type, links } = {}) {
  const serializedLinks = Array.isArray(links)
    ? JSON.stringify(links)
    : typeof links === "string"
      ? links
      : JSON.stringify([]);

  const result = validateDraftOrder({
    order_title,
    order_type: order_type || "normal",
    links: serializedLinks,
  });

  return result.valid ? 1 : 0;
}

export function validateDraftOrder(draftOrder) {
  if (!draftOrder || typeof draftOrder !== "object") {
    return { valid: false, message: "Draft order is unavailable." };
  }

  if (!String(draftOrder.order_title || "").trim()) {
    return {
      valid: false,
      message: draftError(draftOrder, "Please review and fill all the required fields!"),
    };
  }

  let links = [];
  try {
    links = JSON.parse(draftOrder.links || "[]");
  } catch {
    links = [];
  }

  if (!Array.isArray(links) || !links.length) {
    return {
      valid: false,
      message: draftError(draftOrder, "Please review and fill all the required fields!"),
    };
  }

  const orderType = draftOrder.order_type || "normal";
  if (orderType === "media_placement") {
    return validateMediaLinks(links, draftOrder);
  }
  if (orderType === "missed_opportunity") {
    return validateMissedOpportunityLinks(links, draftOrder);
  }

  return validateNormalLinks(links, draftOrder);
}
